import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { Slide } from '../types/slide';
import { DomSanitizer } from '@angular/platform-browser';
import { SlideShowRepository } from '../repositories/slide-show.repository';

export type SlideShowVariant = 'default' | 'locked';

@Injectable()
export class SlideShowService {
  readonly #slideShowRepo = inject(SlideShowRepository);
  readonly sanitizer = inject(DomSanitizer);

  readonly #variant = signal<SlideShowVariant | undefined>(undefined);

  readonly slidesResource = resource({
    params: () => ({ variant: this.#variant() }),
    loader: async ({ params }) => {
      if (params.variant === 'default') return await this.#slideShowRepo.getSlides();

      if (params.variant === 'locked') return await this.#slideShowRepo.getLockedSlides();

      return await this.#slideShowRepo.getSlides();
    },
  });

  readonly #slides = linkedSignal(() => {
    const slides = this.slidesResource.value();
    const map = new Map<number, { slide: Slide; image: any | undefined }>();

    if (!slides) return undefined;

    for (const slide of slides) {
      map.set(slide.id, { slide, image: undefined });
    }

    return map;
  });

  readonly isLoading = computed(() => this.slidesResource.isLoading());
  readonly slides = computed(() => this.#slides());
  readonly slidesArray = computed(() => {
    const value = this.slidesResource.value();
    return value;
  });
  readonly slidesCount = computed(() => this.slidesArray()?.length ?? 0);

  setSlideShowVariant(variant: SlideShowVariant) {
    this.#variant.set(variant);
  }

  async getSlideImage(index: number) {
    const slidesMap = this.slides();
    const slidesArray = this.slidesArray();
    if (!slidesArray) return undefined;

    const imageKey = slidesArray[index].id;
    const imageData = slidesMap?.get(imageKey);

    if (!slidesMap || !imageData || !imageKey) return undefined;

    if (imageData?.image) {
      return slidesMap.get(imageKey);
    }

    const slideImage = await this.#slideShowRepo.getSlideImage(imageKey);

    const unsafeUrl = URL.createObjectURL(slideImage);
    const image = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);

    slidesMap.set(imageKey, { slide: imageData.slide, image: image });

    return slidesMap.get(imageKey);
  }
}

@Injectable()
export class SlideShowDefaultService extends SlideShowService {
  constructor() {
    super();
    this.setSlideShowVariant('default');
  }
}

@Injectable()
export class SlideShowLockedService extends SlideShowService {
  constructor() {
    super();
    this.setSlideShowVariant('locked');
  }
}
